import { SITE_DOMAIN } from '../constants';

export type CalendlyPrefill = {
  name?: string;
  email?: string;
  phone?: string;
};

export type CalendlyUtm = {
  source?: string;
  medium?: string;
  campaign?: string;
};

export type CalendlyScheduledPayload = {
  event: { uri: string };
  invitee: { uri: string };
};

export type CalendlyMessage =
  | { event: 'calendly.event_scheduled'; payload: CalendlyScheduledPayload }
  | { event: 'calendly.date_and_time_selected'; payload: Record<string, unknown> }
  | { event: 'calendly.profile_page_viewed'; payload: Record<string, unknown> }
  | { event: 'calendly.event_type_viewed'; payload: Record<string, unknown> };

/** Induce design tokens for Calendly embed (hex without #). */
export const CALENDLY_EMBED_THEME = {
  background_color: '0e0e0e',
  text_color: 'e2e2e2',
  primary_color: 'cc3300',
} as const;

export function getCalendlyEmbedDomain(hostname = typeof window !== 'undefined' ? window.location.hostname : SITE_DOMAIN) {
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return SITE_DOMAIN;
  }
  return hostname;
}

type BuildCalendlyIframeSrcOptions = {
  prefill?: CalendlyPrefill;
  utm?: CalendlyUtm;
  embedDomain?: string;
};

export function buildCalendlyIframeSrc(
  baseUrl: string,
  { prefill, utm, embedDomain = getCalendlyEmbedDomain() }: BuildCalendlyIframeSrcOptions = {},
) {
  const url = new URL(baseUrl);

  url.searchParams.set('hide_gdpr_banner', '1');
  url.searchParams.set('embed_type', 'Inline');
  url.searchParams.set('embed_domain', embedDomain);
  url.searchParams.set('background_color', CALENDLY_EMBED_THEME.background_color);
  url.searchParams.set('text_color', CALENDLY_EMBED_THEME.text_color);
  url.searchParams.set('primary_color', CALENDLY_EMBED_THEME.primary_color);

  if (prefill?.name) url.searchParams.set('name', prefill.name);
  if (prefill?.email) url.searchParams.set('email', prefill.email);
  if (prefill?.phone) url.searchParams.set('a1', prefill.phone);

  if (utm?.source) url.searchParams.set('utm_source', utm.source);
  if (utm?.medium) url.searchParams.set('utm_medium', utm.medium);
  if (utm?.campaign) url.searchParams.set('utm_campaign', utm.campaign);

  return url.toString();
}

export function isCalendlyMessage(event: MessageEvent): event is MessageEvent<CalendlyMessage> {
  return (
    event.origin === 'https://calendly.com' &&
    typeof event.data === 'object' &&
    event.data !== null &&
    'event' in event.data &&
    typeof (event.data as CalendlyMessage).event === 'string' &&
    (event.data as CalendlyMessage).event.startsWith('calendly.')
  );
}
